<template>
    <main class="public-position-share-map">
        <l-map v-if="balise" ref="leafletMap" class="public-position-share-map__leaflet" :zoom="zoom" :center="center"
            :options="mapOptions" :max-zoom="MAX_ZOOM" :min-zoom="MIN_ZOOM" @update:zoom="onMapZoom"
            @update:center="onMapCenter">
            <l-tile-layer :url="tileLayerUrl" :max-zoom="MAX_ZOOM" />

            <l-control-scale position="bottomright" :imperial="false" :metric="true" />

            <l-control-zoom position="bottomright" />

            <BaliseMarker :key="markerKey" :balise="balise" @click="openRightPanel" />
        </l-map>

        <MapSidePanelRight v-if="balise" v-model="rightPanelOpen">
            <RightVehiclePanel v-if="selectedBalise" :balise="selectedBalise" :followMode="followMode" :readOnly="true"
                :publicMode="true" :showHistoryReport="false" @follow="startFollow" @unfollow="stopFollow" />
        </MapSidePanelRight>

        <div v-if="loading && !balise" class="public-position-share-map__state">
            Chargement de la position…
        </div>

        <div v-else-if="errorMessage && !balise" class="public-position-share-map__state">
            <div class="public-position-share-map__error">
                <div class="public-position-share-map__error-title">
                    {{ errorMessage }}
                </div>

                <div v-if="invalidLink" class="public-position-share-map__contact">
                    <div class="public-position-share-map__contact-text">
                        Pour obtenir un nouveau lien de partage, contactez SMS au :
                    </div>

                    <a class="public-position-share-map__contact-phone" href="tel:+33134388638">
                        01 34 38 86 38
                    </a>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import "leaflet/dist/leaflet.css";

import {
    LMap,
    LTileLayer,
    LControlScale,
    LControlZoom
} from "@vue-leaflet/vue-leaflet";

import BaliseMarker from "./Components/BaliseMarker.vue";
import MapSidePanelRight from "./Components/MapSidePanelRight.vue";
import RightVehiclePanel from "./Components/rightPanel/RightVehiclePanel.vue";
import PublicPositionShareService from "@/services/publicPositionShare.service.js";

const DEFAULT_CENTER = [48.9571487, 2.1941876];
const DEFAULT_ZOOM = 16;
const REFRESH_INTERVAL_MS = 5000;

const MIN_ZOOM = 3;
const MAX_ZOOM = 19;

export default {
    name: "PublicPositionShareMap",

    components: {
        LMap,
        LTileLayer,
        LControlScale,
        LControlZoom,
        BaliseMarker,
        MapSidePanelRight,
        RightVehiclePanel
    },

    data() {
        return {
            MIN_ZOOM,
            MAX_ZOOM,

            balise: null,
            selectedBalise: null,

            loading: true,
            errorMessage: "",
            invalidLink: false,

            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM,

            rightPanelOpen: false,
            refreshTimer: null,

            hasInitializedMap: false,
            followMode: true,

            tileLayerUrl:
                "https://api.maptiler.com/maps/bright-v2/{z}/{x}/{y}.png?key=EevE8zHrA8OKNsj637Ms",

            mapOptions: {
                attributionControl: false,
                zoomControl: false
            }
        };
    },

    computed: {
        token() {
            return this.$route.params.token;
        },

        markerKey() {
            if (!this.balise) {
                return "public-position-share-marker";
            }

            return [
                this.balise.id,
                this.balise.lastLatitude,
                this.balise.lastLongitude,
                this.balise.lastPositionDate
            ].join("-");
        }
    },

    created() {
        this.loadPosition();

        this.refreshTimer = setInterval(
            this.loadPosition,
            REFRESH_INTERVAL_MS
        );
    },

    beforeUnmount() {
        this.stopRefresh();
    },

    methods: {
        async loadPosition() {
            if (!this.token) {
                this.loading = false;
                this.errorMessage = "Lien de partage invalide.";
                this.invalidLink = true;
                this.stopRefresh();
                return;
            }

            try {
                const response =
                    await PublicPositionShareService.getPublicPosition(
                        this.token
                    );

                const balise = this.normalizeBalise(response.data);

                if (!balise) {
                    this.handleUnavailablePosition();
                    return;
                }

                this.balise = balise;
                this.errorMessage = "";
                this.invalidLink = false;

                if (this.rightPanelOpen) {
                    this.selectedBalise = balise;
                }

                /*
                 * En mode suivi, la carte reste centrée sur le véhicule.
                 */
                if (this.followMode) {
                    this.center = [
                        balise.lastLatitude,
                        balise.lastLongitude
                    ];
                }

                /*
                 * Le zoom initial n'est défini qu'au premier chargement.
                 * Ensuite, on conserve le niveau de zoom choisi.
                 */
                if (!this.hasInitializedMap) {
                    this.zoom = DEFAULT_ZOOM;
                    this.hasInitializedMap = true;
                }
            } catch (error) {
                this.handleRequestError(error);
            } finally {
                this.loading = false;
            }
        },

        normalizeBalise(payload) {
            /*
             * Format actuellement renvoyé par le contrôleur public :
             *
             * {
             *   expiresAt,
             *   vehicle: {
             *     id,
             *     name,
             *     registrationPlate,
             *     icon
             *   },
             *   position: {
             *     latitude,
             *     longitude,
             *     address,
             *     date,
             *     speed
             *   }
             * }
             */
            if (payload?.vehicle && payload?.position) {
                const latitude = Number(
                    payload.position.latitude
                );

                const longitude = Number(
                    payload.position.longitude
                );

                if (
                    !Number.isFinite(latitude)
                    || !Number.isFinite(longitude)
                    || latitude === 0
                    || longitude === 0
                ) {
                    return null;
                }

                return {
                    ...payload.vehicle,

                    lastLatitude: latitude,
                    lastLongitude: longitude,

                    lastAddress:
                        payload.position.address
                        ?? null,

                    lastPositionDate:
                        payload.position.date
                        ?? null,

                    lastSpeed:
                        payload.position.speed
                        ?? null,

                    /*
                     * Ces champs ne sont pas encore renvoyés par la route
                     * publique, mais leur présence évite des valeurs
                     * indéfinies dans les composants partagés.
                     */
                    lastDirection:
                        payload.position.direction
                        ?? null,

                    lastBattery:
                        payload.position.battery
                        ?? null,

                    lastPowerVoltage:
                        payload.position.powerVoltage
                        ?? null,

                    lastLevelGPS:
                        payload.position.levelGPS
                        ?? null,

                    lastLevelGSM:
                        payload.position.levelGSM
                        ?? null,

                    shareExpiresAt:
                        payload.expiresAt
                        ?? null
                };
            }

            /*
             * Compatibilité avec d'éventuels anciens formats de réponse.
             */
            const source =
                payload?.balise
                || payload?.position
                || payload?.data
                || payload;

            const latitude = Number(
                source?.lastLatitude
                ?? source?.latitude
            );

            const longitude = Number(
                source?.lastLongitude
                ?? source?.longitude
            );

            if (
                !Number.isFinite(latitude)
                || !Number.isFinite(longitude)
                || latitude === 0
                || longitude === 0
            ) {
                return null;
            }

            /*
             * BaliseMarker et RightVehiclePanel travaillent avec la forme
             * standard d'une balise, notamment lastLatitude et
             * lastLongitude.
             */
            return {
                ...source,

                id:
                    source.id
                    ?? source.baliseId,

                name:
                    source.name
                    ?? source.vehicleName
                    ?? source.baliseName
                    ?? "Véhicule",

                lastLatitude: latitude,
                lastLongitude: longitude,

                lastPositionDate:
                    source.lastPositionDate
                    ?? source.positionDate
                    ?? source.date
                    ?? null,

                lastSpeed:
                    source.lastSpeed
                    ?? source.speed
                    ?? null,

                lastDirection:
                    source.lastDirection
                    ?? source.direction
                    ?? null,

                lastBattery:
                    source.lastBattery
                    ?? source.battery
                    ?? null,

                lastPowerVoltage:
                    source.lastPowerVoltage
                    ?? source.powerVoltage
                    ?? null,

                lastLevelGPS:
                    source.lastLevelGPS
                    ?? source.levelGPS
                    ?? null,

                lastLevelGSM:
                    source.lastLevelGSM
                    ?? source.levelGSM
                    ?? null,

                lastAddress:
                    source.lastAddress
                    ?? source.address
                    ?? null
            };
        },

        handleUnavailablePosition() {
            if (!this.balise) {
                this.invalidLink = false;
                this.errorMessage =
                    "Aucune position n’est disponible pour ce véhicule.";
            }
        },

        handleRequestError(error) {
            const status = error.response?.status;
            const serverMessage =
                error.response?.data?.message;

            /*
             * Le back retourne volontairement la même réponse 404 pour :
             * - un token inconnu ;
             * - un lien expiré ;
             * - un lien révoqué.
             */
            if (status === 404) {
                this.balise = null;
                this.selectedBalise = null;
                this.rightPanelOpen = false;
                this.invalidLink = true;

                this.errorMessage =
                    serverMessage
                    || "Ce lien de partage est invalide ou expiré.";

                this.stopRefresh();
                return;
            }

            console.error(
                "Impossible de charger la position publique :",
                error
            );

            this.invalidLink = false;

            /*
             * Lors d'une erreur réseau temporaire, on conserve la dernière
             * position connue au lieu de retirer le véhicule de la carte.
             */
            if (!this.balise) {
                this.errorMessage =
                    serverMessage
                    || "Impossible de charger la position du véhicule.";
            }
        },

        openRightPanel() {
            if (!this.balise) {
                return;
            }

            this.selectedBalise = this.balise;
            this.rightPanelOpen = true;
        },

        startFollow() {
            if (!this.balise) {
                return;
            }

            this.followMode = true;

            this.center = [
                this.balise.lastLatitude,
                this.balise.lastLongitude
            ];
        },

        stopFollow() {
            this.followMode = false;
        },

        onMapZoom(zoom) {
            this.zoom = zoom;

            /*
             * Les événements d'initialisation de Leaflet peuvent être
             * déclenchés avant que la carte soit réellement affichée.
             */
            if (this.hasInitializedMap) {
                this.followMode = false;
            }
        },

        onMapCenter(center) {
            if (Array.isArray(center)) {
                this.center = center;

                if (this.hasInitializedMap) {
                    this.followMode = false;
                }

                return;
            }

            if (
                center
                && Number.isFinite(center.lat)
                && Number.isFinite(center.lng)
            ) {
                this.center = [
                    center.lat,
                    center.lng
                ];

                if (this.hasInitializedMap) {
                    this.followMode = false;
                }
            }
        },

        stopRefresh() {
            if (!this.refreshTimer) {
                return;
            }

            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
};
</script>

<style scoped>
.public-position-share-map {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: #f2f2f2;
}

.public-position-share-map__leaflet {
    width: 100%;
    height: 100%;
}

.public-position-share-map__state {
    position: absolute;
    inset: 0;
    z-index: 3000;

    display: flex;
    align-items: center;
    justify-content: center;

    padding: 1.5rem;
    color: #333;
    text-align: center;
    background-color: #f2f2f2;
}

.public-position-share-map__error {
    max-width: 32rem;
    padding: 1.25rem 1.5rem;

    background-color: #fff;
    border: 1px solid #d9dee5;
    border-radius: 0.5rem;
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
}

.public-position-share-map__error-title {
    color: #b42318;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.4;
}

.public-position-share-map__contact {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}

.public-position-share-map__contact-text {
    color: #5f6875;
    font-size: 0.82rem;
    line-height: 1.5;
}

.public-position-share-map__contact-phone {
    display: inline-block;
    margin-top: 0.35rem;

    color: #2468c9;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.3;
    text-decoration: none;
}

.public-position-share-map__contact-phone:hover {
    color: #174d99;
    text-decoration: underline;
}

.public-position-share-map__contact-phone:focus-visible {
    outline: 2px solid #2468c9;
    outline-offset: 3px;
    border-radius: 0.2rem;
}

/*
 * Même positionnement des contrôles que sur BaseMapShell,
 * mais sans dépendre du header de l'application.
 */
:deep(.leaflet-bottom.leaflet-right) {
    position: fixed;
    right: 0.75rem;
    bottom: 0.3rem;
    z-index: 1800;
}

@media (max-width: 575.98px) {
    .public-position-share-map__state {
        padding: 1rem;
    }

    .public-position-share-map__error {
        width: 100%;
        padding: 1.1rem 1.2rem;
    }

    .public-position-share-map__error-title {
        font-size: 0.9rem;
    }

    .public-position-share-map__contact-text {
        font-size: 0.8rem;
    }

    .public-position-share-map__contact-phone {
        font-size: 0.95rem;
    }
}

@media (min-width: 769px) {
    :deep(.leaflet-bottom.leaflet-right) {
        bottom: 0.5rem;
    }
}
</style>